import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle
} from "react-native";

import type { DeviceFormFactor } from "@recital-master/shared";

import { MonitorIcon, PhoneIcon, TabletIcon } from "@/design/icons";
import { webTouchControl } from "@/design/interactionMotion";
import { Muted, Text } from "@/design/Text";
import { palette, radius, space } from "@/design/tokens";
import { DOT_SEPARATOR } from "@/lib/format";

export type DeviceRowData = {
  deviceId: string;
  label: string;
  platform: string;
  formFactor: DeviceFormFactor;
  locationLabel?: string | null;
  lastUsed?: string | null;
  thisDevice?: boolean;
};

export function deviceDateLabel(value: string): string {
  const date = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  // Recency is the signal; the year only earns its width when it isn't obvious.
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" })
  });
}

function DeviceIcon({ formFactor, color }: { formFactor: DeviceFormFactor; color: string }) {
  if (formFactor === "computer") return <MonitorIcon size={18} color={color} />;
  if (formFactor === "tablet") return <TabletIcon size={18} color={color} />;
  return <PhoneIcon size={18} color={color} />;
}

export function DeviceRow({
  device,
  actionLabel,
  pending = false,
  actionDisabled = false,
  onAction,
  error,
  secondaryLine,
  ghost = false,
  compact = false,
  style
}: {
  device: DeviceRowData;
  actionLabel?: string;
  pending?: boolean;
  actionDisabled?: boolean;
  onAction?: () => void;
  error?: string | null;
  secondaryLine?: ReactNode;
  ghost?: boolean;
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const disabled = pending || actionDisabled;
  const iconColor = ghost ? palette.faint : palette.textSoft;

  return (
    <View
      style={[
        styles.row,
        compact && styles.rowCompact,
        ghost && styles.rowGhost,
        pending && styles.rowPending,
        style
      ]}
    >
      <View style={[styles.badge, compact && styles.badgeCompact, ghost && styles.badgeGhost]}>
        <DeviceIcon formFactor={device.formFactor} color={iconColor} />
      </View>
      <View style={styles.body}>
        <Text
          variant="bodyStrong"
          color={ghost ? palette.muted : palette.text}
          numberOfLines={1}
        >
          {device.label}
        </Text>
        {secondaryLine ? (
          typeof secondaryLine === "string" ? (
            <Muted variant="small">{secondaryLine}</Muted>
          ) : (
            secondaryLine
          )
        ) : device.thisDevice ? (
          <Muted variant="small" numberOfLines={1}>
            {device.platform}
            {DOT_SEPARATOR}
            <Text variant="small" color={palette.accent}>
              This device
            </Text>
          </Muted>
        ) : (
          <Muted variant="small" numberOfLines={1}>
            {device.platform}
            {device.locationLabel ? `${DOT_SEPARATOR}${device.locationLabel}` : null}
          </Muted>
        )}
        {device.lastUsed ? (
          <Muted variant="small" numberOfLines={1} style={styles.lastUsed}>
            Last used {device.lastUsed}
          </Muted>
        ) : null}
        {error ? (
          <Text variant="small" color={palette.danger}>
            {error}
          </Text>
        ) : null}
      </View>
      {actionLabel ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${actionLabel} ${device.label}`}
          accessibilityState={{ busy: pending, disabled }}
          disabled={disabled}
          onPress={onAction}
          style={({ hovered, focused }) => [
            styles.action,
            webTouchControl,
            compact && styles.actionCompact,
            !disabled && (hovered || focused) && styles.actionHovered,
            actionDisabled && !pending && styles.actionDisabled
          ]}
        >
          {/* Preserve the settled control's geometry while pending. Loading is
              status decoration, not replacement content that may resize a row. */}
          <View style={pending && styles.actionContentHidden}>
            <Text variant="smallStrong" color={palette.muted}>
              {actionLabel}
            </Text>
          </View>
          {pending ? (
            <View style={styles.spinnerOverlay}>
              <ActivityIndicator size="small" color={palette.textSoft} />
            </View>
          ) : null}
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.lg
  },
  rowCompact: {
    backgroundColor: palette.coolWashSubtle,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    gap: space.sm,
    padding: space.md
  },
  rowGhost: {
    backgroundColor: palette.coolWashFaint,
    borderColor: palette.hairlineStrong,
    borderRadius: radius.md,
    borderStyle: "dashed",
    borderWidth: 1,
    opacity: 0.7,
    padding: space.md
  },
  rowPending: {
    opacity: 0.5
  },
  badge: {
    alignItems: "center",
    backgroundColor: palette.surfaceSunken,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    height: 40,
    justifyContent: "center",
    width: 40
  },
  badgeCompact: {
    height: 36,
    width: 36
  },
  badgeGhost: {
    backgroundColor: palette.coolWashFaint
  },
  body: {
    flex: 1,
    gap: 1,
    minWidth: 0
  },
  lastUsed: {
    color: palette.faint
  },
  action: {
    alignItems: "center",
    borderColor: palette.hairlineStrong,
    borderRadius: radius.pill,
    borderWidth: 1,
    flexDirection: "row",
    gap: 6,
    justifyContent: "center",
    minWidth: 76,
    paddingHorizontal: space.md,
    paddingVertical: 4
  },
  actionCompact: {
    minHeight: 34,
    minWidth: 80
  },
  actionHovered: {
    backgroundColor: palette.coolWashHover
  },
  actionContentHidden: {
    opacity: 0
  },
  actionDisabled: {
    opacity: 0.4
  },
  spinnerOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center"
  }
});
